import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { UseOutletContextAsset } from './Asset';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { AssetDA } from '../../../../services/admin/asset.service';
import { Asset, AssetStatus } from '../../../../models/asset.model';
import { Error } from '../../../../models/error.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { getError } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';
import { formatMoney } from '../../../../scripts/format.value.script';
import { modalShow } from '../../../../utils/modal.util';

import AppPreloader from '../../../../components/Preloader';
import AppModalAssetRent from '../../../../components/modal/ModalAssetRent';

export interface AssetViewPageProps {};

let errorResponse: Error;

const AssetViewPage: React.FunctionComponent<AssetViewPageProps> = props => {
  const {setRoute, appServiceForUser, companyForUser, categoriesForCompany, servicesForCompany, storageCentersForCompany} = UseOutletContextAsset()
  const {lang} = StateLanguage()
  const navigate = useNavigate()
  const param = useParams()

  const [mounted, setMounted] = useState(false)
  const [asset, setAsset] = useState<Asset | null>(null)

  const loadAsset = async (id_company: string, id_asset: string) => {
    try {
      const response = await AssetDA.getAsset(id_company, id_asset)
      if (response.status === 200) {
        setAsset(response.data)
      } else {
        errorResponse = response.data
        GlobalSwal.fire({
          title: getError(errorResponse?.code, lang.code, errorResponse?.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    } catch (error) {
      errorWebHandler(lang, error)
    }
  }

  const submitAssetDelete = async () => {
    if (!companyForUser || !asset) return

    GlobalSwal.fire({
      title: lang.labels.youWantToDeleteTheAsset,
      text: lang.labels.youWillNotBeAbleToUndo,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: lang.labels.yesDelete,
      cancelButtonText: lang.labels.noCancel
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await AssetDA.deleteAsset(companyForUser.id, asset.id)
          if (response.status === 200) {
            GlobalSwal.fire({
              title: lang.labels.assetDeletedSuccess,
              icon: 'success',
              showConfirmButton: false,
              timer: 1500
            }).then(() => {
              navigate('/main/inventory/asset/list', {replace: true})
            })
          } else {
            errorResponse = response.data
            GlobalSwal.fire({
              title: getError(errorResponse?.code, lang.code, errorResponse?.message),
              text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
              icon: 'error',
              confirmButtonText: lang.labels.okGotIt
            })
          }
        } catch (error) {
          errorWebHandler(lang, error)
        }
      }
    })
  }

  const statusBadge = (status: AssetStatus): {label: string, variant: string} => {
    switch (status) {
      case 'available': return {label: lang.labels.assetStatusAvailable, variant: 'success'}
      case 'rented': return {label: lang.labels.assetStatusRented, variant: 'warning'}
      case 'out_of_service': return {label: lang.labels.assetStatusOutOfService, variant: 'danger'}
      default: return {label: status, variant: 'secondary'}
    }
  }

  const categoryName = (id: string): string => {
    const cat = categoriesForCompany?.find(c => c.id === id)
    return cat?.display_name ?? cat?.name ?? '-'
  }

  const cadenceLabel = (unit: string): string => {
    const map: Record<string, string> = {
      day: lang.labels.cadenceDay,
      week: lang.labels.cadenceWeek,
      month: lang.labels.cadenceMonth,
      quarter: lang.labels.cadenceQuarter,
      year: lang.labels.cadenceYear,
    }
    return map[unit] ?? unit
  }

  const storageCenterName = (id: string): string => {
    const sc = storageCentersForCompany?.find(s => s.id === id)
    return sc?.name ?? '-'
  }

  useEffect(() => {
    if (companyForUser && param.id) {
      loadAsset(companyForUser.id, param.id)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect(() => {
    setMounted(true)
    setRoute({path: {root: lang.labels.assets, branch: lang.labels.viewAsset}, company: true})
    return () => setMounted(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { asset
        ?
        <>
          <div className="card card-flush mb-5">
            <div className="card-header">
              <div className="card-title">
                <h3>{asset.name}</h3>
                { asset.code &&
                  <span className="badge badge-light ms-3">{asset.code}</span>
                }
              </div>
              <div className="card-toolbar">
                <div className="d-flex justify-content-end align-items-center">
                  { asset.status === 'available' &&
                    <button className="btn btn-success me-2" type="button" onClick={() => modalShow('modal-asset-rent')}>
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 ms-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 14H18V10H20C20.6 10 21 10.4 21 11V13C21 13.6 20.6 14 20 14ZM21 19V17C21 16.4 20.6 16 20 16H18V20H20C20.6 20 21 19.6 21 19ZM21 7V5C21 4.4 20.6 4 20 4H18V8H20C20.6 8 21 7.6 21 7ZM10 2H4C2.9 2 2 2.9 2 4V20C2 21.1 2.9 22 4 22H10C11.1 22 12 21.1 12 20V4C12 2.9 11.1 2 10 2ZM7 20C6.2 20 5.5 19.3 5.5 18.5C5.5 17.7 6.2 17 7 17C7.8 17 8.5 17.7 8.5 18.5C8.5 19.3 7.8 20 7 20ZM10 15H4V5H10V15Z" fill="black"/>
                            <path opacity="0.3" d="M16 2H14V22H16C17.1 22 18 21.1 18 20V4C18 2.9 17.1 2 16 2Z" fill="black"/>
                          </svg>
                        </span>
                        {lang.labels.rentAsset}
                      </div>
                    </button>
                  }
                  <Link to={"/main/inventory/asset/update/" + asset.id} className="btn btn-primary me-2">
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" fillRule="evenodd" clipRule="evenodd" d="M2 4.63158C2 3.1782 3.1782 2 4.63158 2H13.47C14.0155 2 14.278 2.66919 13.8778 3.04006L12.4556 4.35821C11.9009 4.87228 11.1726 5.15789 10.4163 5.15789H7.1579C6.05333 5.15789 5.15789 6.05333 5.15789 7.1579V16.8421C5.15789 17.9467 6.05333 18.8421 7.1579 18.8421H16.8421C17.9467 18.8421 18.8421 17.9467 18.8421 16.8421V13.7518C18.8421 12.927 19.1817 12.1387 19.7809 11.572L20.9878 10.4308C21.3703 10.0691 22 10.3403 22 10.8668V19.3684C22 20.8218 20.8218 22 19.3684 22H4.63158C3.1782 22 2 20.8218 2 19.3684V4.63158Z" fill="black"/>
                          <path d="M10.9256 11.1882C10.5351 10.7977 10.5351 10.1645 10.9256 9.77397L18.0669 2.6327C18.8479 1.85165 20.1143 1.85165 20.8953 2.6327L21.3665 3.10391C22.1476 3.88496 22.1476 5.15129 21.3665 5.93234L14.2252 13.0736C13.8347 13.4641 13.2016 13.4641 12.811 13.0736L10.9256 11.1882Z" fill="black"/>
                          <path d="M8.82343 12.0064L8.08852 14.3348C7.8655 15.0414 8.46151 15.7366 9.19388 15.6242L11.8974 15.2092C12.4642 15.1222 12.6916 14.4278 12.2861 14.0223L9.98595 11.7221C9.61452 11.3507 8.98154 11.5055 8.82343 12.0064Z" fill="black"/>
                        </svg>
                      </span>
                      {lang.labels.edit}
                    </div>
                  </Link>
                  { asset.status !== 'rented' &&
                    <button className="btn btn-primary" type="button" onClick={submitAssetDelete}>
                      <div className="d-flex align-items-center">
                        <span className="svg-icon svg-icon-2 ms-0">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 9C5 8.44772 5.44772 8 6 8H18C18.5523 8 19 8.44772 19 9V18C19 19.6569 17.6569 21 16 21H8C6.34315 21 5 19.6569 5 18V9Z" fill="black"/>
                            <path opacity="0.5" d="M5 5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V5C19 5.55228 18.5523 6 18 6H6C5.44772 6 5 5.55228 5 5V5Z" fill="black"/>
                            <path opacity="0.5" d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V4H9V4Z" fill="black"/>
                          </svg>
                        </span>
                        {lang.labels.delete}
                      </div>
                    </button>
                  }
                </div>
              </div>
            </div>
            <div className="card-body pt-0">
              <div className="d-flex flex-wrap">
                <div className="flex-equal me-5">
                  <table className="table align-middle gy-0 m-0">
                    <tbody className="fw-bold text-secondary">
                      <tr>
                        <td className="w-200px text-black">{lang.labels.assetName}</td>
                        <td className="min-w-200px fs-6">{asset.name}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-black">{lang.labels.assetCode}</td>
                        <td className="min-w-200px fs-6">{asset.code || '-'}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-black">{lang.labels.assetDescription}</td>
                        <td className="min-w-200px fs-6">{asset.description || '-'}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-black">{lang.labels.category}</td>
                        <td className="min-w-200px fs-6">{categoryName(asset.id_category)}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-black align-top">{lang.labels.rentalOptions}</td>
                        <td className="min-w-200px fs-6">
                          { (asset.rental_options ?? []).length === 0
                            ?
                            <span className="text-muted">{lang.labels.noRentalOption}</span>
                            :
                            <div className="d-flex flex-column gap-1">
                              { asset.rental_options.map((opt, i) => {
                                const sid = typeof opt.id_service === 'string' ? opt.id_service : (opt.id_service as any)?.toString()
                                const svc = servicesForCompany?.find(s => s.id === sid)
                                const price = svc?.price_sale?.amount != null ? `${svc.price_sale.amount.toFixed(2)} ${companyForUser?.currency?.code ?? 'CHF'}` : '—'
                                return (
                                  <div key={i}>
                                    <span className="fw-bold">{cadenceLabel(opt.cadence_unit)}</span>
                                    {' — '}{svc?.display_name ?? svc?.name ?? '?'}
                                    {' — '}<span style={{fontVariantNumeric: 'tabular-nums'}}>{price}</span>
                                  </div>
                                )
                              })}
                            </div>
                          }
                        </td>
                      </tr>
                      <tr>
                        <td className="w-200px text-black">{lang.labels.storageCenter}</td>
                        <td className="min-w-200px fs-6">{storageCenterName(asset.id_storage_center)}</td>
                      </tr>
                      <tr>
                        <td className="w-200px text-black">{lang.labels.defaultDepositAmount}</td>
                        <td className="min-w-200px fs-6" style={{fontVariantNumeric: 'tabular-nums'}}>{formatMoney(asset.default_deposit_amount ?? 0)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="flex-equal">
                  <table className="table align-middle gy-0 m-0">
                    <tbody className="fw-bold text-secondary">
                      <tr>
                        <td className="w-200px text-black">{lang.labels.status}</td>
                        <td className="min-w-200px fs-6">
                          { (() => {
                            const badge = statusBadge(asset.status)
                            return <span className={`badge badge-light-${badge.variant}`}>{badge.label}</span>
                          })()}
                        </td>
                      </tr>
                      { asset.status === 'rented' && asset.current_tenant &&
                        <>
                          <tr>
                            <td className="w-200px text-black">{lang.labels.currentTenant}</td>
                            <td className="min-w-200px fs-6">{asset.current_tenant.name}</td>
                          </tr>
                          { asset.id_current_contract &&
                            <tr>
                              <td className="w-200px text-black">{lang.labels.recurringContract}</td>
                              <td className="min-w-200px fs-6">
                                <Link to={"/main/sale/recurring-contract/view/" + asset.id_current_contract} className="text-primary text-hover-dark">
                                  {lang.labels.view}
                                </Link>
                              </td>
                            </tr>
                          }
                          { asset.rented_since &&
                            <tr>
                              <td className="w-200px text-black">{lang.labels.rentedSince}</td>
                              <td className="min-w-200px fs-6">{asset.rented_since}</td>
                            </tr>
                          }
                          { asset.rented_until &&
                            <tr>
                              <td className="w-200px text-black">{lang.labels.rentedUntil}</td>
                              <td className="min-w-200px fs-6">{asset.rented_until}</td>
                            </tr>
                          }
                        </>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          { asset.is_depreciable &&
            <div className="card card-flush">
              <div className="card-header">
                <div className="card-title">
                  <h4 className="fw-bolder">{lang.labels.depreciationSection}</h4>
                </div>
              </div>
              <div className="card-body pt-0">
                <table className="table align-middle gy-0 m-0">
                  <tbody className="fw-bold text-secondary">
                    <tr>
                      <td className="w-200px text-black">{lang.labels.acquisitionValue}</td>
                      <td className="min-w-200px fs-6" style={{fontVariantNumeric: 'tabular-nums'}}>{formatMoney(asset.acquisition_value ?? 0)}</td>
                    </tr>
                    <tr>
                      <td className="w-200px text-black">{lang.labels.acquisitionDate}</td>
                      <td className="min-w-200px fs-6">{asset.acquisition_date || '-'}</td>
                    </tr>
                    <tr>
                      <td className="w-200px text-black">{lang.labels.residualValue}</td>
                      <td className="min-w-200px fs-6" style={{fontVariantNumeric: 'tabular-nums'}}>{formatMoney(asset.residual_value ?? 0)}</td>
                    </tr>
                    <tr>
                      <td className="w-200px text-black">{lang.labels.depreciationYears}</td>
                      <td className="min-w-200px fs-6" style={{fontVariantNumeric: 'tabular-nums'}}>{asset.depreciation_years ?? 0}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          }
          <AppModalAssetRent appServiceForUser={appServiceForUser} companyForUser={companyForUser} asset={asset} servicesForCompany={servicesForCompany}></AppModalAssetRent>
        </>
        :
        <AppPreloader></AppPreloader>
      }
    </div>
  )
};

export default AssetViewPage;
